import React, { useReducer, useState, useMemo } from "react";
import {
  List,
  Badge,
  Typography,
  Spin,
  Select,
  Card,
  Space,
  Tag,
  Input,
  Empty,
  message,
} from "antd";
import { BellOutlined } from "@ant-design/icons";
import { updateNotificationStatus } from "../../../Services/NotificationService";
import { FETCH_ALL_NOTIFICATION_API } from "../../../Constants/Apis";
import { useFetch } from "../../../Utils/useFetch";
import { reducer } from "../../../Utils/CommonUtils";

const { Text, Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const statusColors = {
  New: { color: "blue" },
  Ongoing: { color: "orange" },
  Done: { color: "green" },
};
const statusOptions = ["All", "New", "Ongoing", "Done"];
const messageFilterOptions = ["All", "SURAT JALAN", "Penjualan Bermasalah"];

const NotificationPage = ({ history, userData }) => {
  const initialState = {
    searchKeyword: "",
    filteredInfo: {},
    sorter: {},
    current: 1,
    pageSize: 10,
  };
  const [state, dispatch] = useReducer(reducer, initialState);

  const [statusFilter, setStatusFilter] = useState("All");
  const [messageFilter, setMessageFilter] = useState("All");

  // Build API url in the same way as ShippingPage (useFetch(url, state))
  const notificationApiUrl = useMemo(() => {
    // messageFilter is an extra server param (not part of the generic state)
    const params = new URLSearchParams({ messageFilter }).toString();
    return `${FETCH_ALL_NOTIFICATION_API}/status/${encodeURIComponent(
      statusFilter
    )}?${params}`;
  }, [statusFilter, messageFilter]);

  const { fetchedData, callReFetch, loading } = useFetch(
    notificationApiUrl,
    state
  );

  // --- robust unwrap for data?.data pattern (and {data,total})
  const raw = fetchedData?.data ?? fetchedData;
  const body = raw?.data ?? raw;

  const notificationData = Array.isArray(body)
    ? body
    : Array.isArray(body?.data)
    ? body.data
    : [];

  const backendTotal =
    (typeof body?.total === "number" && body.total) ??
    (typeof raw?.total === "number" && raw.total) ??
    notificationData.length;

  const paginationData = raw?.pagination || {
    current: state.current,
    pageSize: state.pageSize,
    total: backendTotal,
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    // reset to page 1 like your ShippingPage does when filters change
    dispatch({ type: "current", payload: 1 });
    callReFetch();
  };

  const handleMessageFilterChange = (value) => {
    setMessageFilter(value);
    dispatch({ type: "current", payload: 1 });
    callReFetch();
  };

  const handleSearch = (value) => {
    dispatch({ type: "searchKeyword", payload: value || "" });
    dispatch({ type: "current", payload: 1 });
    callReFetch();
  };

  // If later you add table sorting/filters, follow the same pattern as ShippingPage’s handleTableChange.
  // Here we only keep pagination sync with server like your ShippingTable usage.
  const onListPageChange = (page, pageSize) => {
    dispatch({ type: "current", payload: page });
    dispatch({ type: "pageSize", payload: pageSize });
    callReFetch();
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateNotificationStatus(id, status);
      message.success("Status updated");
      callReFetch();
    } catch (err) {
      message.error("Failed to update status");
      console.error(err);
    }
  };

  // derive a short title from the message (since you don’t have title/isRead)
  const deriveTitle = (msg = "") => {
    const firstSentence = String(msg).split(".")[0]?.trim();
    return firstSentence || "Notification";
  };

  return (
    <div
      style={{
        width: "100%",
        padding: "20px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflowX: "hidden",
      }}
    >
      {/* Toolbar (mirrors ShippingPage layout) */}
      <div
        style={{
          paddingBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "1200px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Space align="center" wrap>
          <BellOutlined style={{ fontSize: 22, color: "#1890ff" }} />
          <Title level={4} style={{ margin: 0 }}>
            Notifications
          </Title>
        </Space>

        <Select
          value={statusFilter}
          style={{ minWidth: 160 }}
          placeholder="Status"
          onChange={handleStatusFilterChange}
        >
          {statusOptions.map((s) => (
            <Option key={s} value={s}>
              {s}
            </Option>
          ))}
        </Select>

        <Select
          value={messageFilter}
          style={{ minWidth: 220 }}
          placeholder="Message Type"
          onChange={handleMessageFilterChange}
        >
          {messageFilterOptions.map((s) => (
            <Option key={s} value={s}>
              {s}
            </Option>
          ))}
        </Select>

        <Search
          placeholder="Search..."
          allowClear
          onSearch={handleSearch}
          style={{ width: 300 }}
        />
      </div>

      <Card
        bordered={false}
        style={{
          width: "100%",
          maxWidth: "1200px",
          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
          borderRadius: "8px",
        }}
      >
        <Spin spinning={loading}>
          <List
            itemLayout="horizontal"
            dataSource={notificationData}
            locale={{
              emptyText: <Empty description="No notifications found" />,
            }}
            pagination={{
              current: paginationData.current,
              pageSize: paginationData.pageSize,
              total: paginationData.total,
              showSizeChanger: true,
              onChange: onListPageChange,
            }}
            style={{ marginTop: 16 }}
            renderItem={(item) => {
              const title = deriveTitle(item.message);
              const status = item.status || "New";
              const isNew = status === "New";

              return (
                <List.Item
                  style={{
                    padding: 16,
                    marginBottom: 8,
                    borderRadius: 6,
                    backgroundColor: isNew ? "#fafafa" : "#fff",
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
                    transition: "all 0.3s",
                  }}
                  actions={[
                    <Select
                      key="status"
                      value={status}
                      style={{ width: 140 }}
                      onChange={(value) => handleStatusChange(item._id, value)}
                    >
                      {statusOptions
                        .filter((o) => o !== "All")
                        .map((opt) => (
                          <Option key={opt} value={opt}>
                            {opt}
                          </Option>
                        ))}
                    </Select>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Badge
                        dot
                        status={isNew ? "processing" : "default"}
                        style={{ marginTop: 8 }}
                      />
                    }
                    title={
                      <Space>
                        <Text strong={isNew} style={{ fontSize: 16 }}>
                          {title}
                        </Text>
                        <Tag color={statusColors[status]?.color || "default"}>
                          {status}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space
                        direction="vertical"
                        size={4}
                        style={{ marginTop: 8 }}
                      >
                        <Text style={{ color: "#595959" }}>{item.message}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleString()
                            : ""}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default NotificationPage;
